<template>
  <div
    class="tw-bg-sf-primary-subtle"
    :class="borderRound"
    :data-testid="testId"
  >
    <div class="tw-py-3.5 tw-px-4">
      <div>
        <div class="tw-gap-2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-flex-col tw-gap-3">
            <div class="tw-flex tw-gap-2 tw-items-center tw-text-sm">
              <i
                class="
                  sf-pointer
                  icon-chevron-right
                  tw-w-4
                  tw-text-sf-gray-darker
                  tw-text-xs
                  tw-flex
                  tw-justify-center
                "
                :class="chevronStyling"
                data-testid="chevron-dropdown"
                @click="toggleExpand"
              ></i>
              <i
                class="icon icon-flask tw-text-sf-primary-dark tw-text-base"
              ></i>
              <span class="tw-font-medium tw-leading-4 tw-text-[14px]">{{
                title
              }}</span>
              <span class="tw-font-normal tw-leading-4 tw-text-[14px]">{{
                subtitle
              }}</span>
            </div>
            <div v-if="!expanded" class="tw-flex">
              <div
                class="
                  documents-list
                  tw-flex
                  tw-flex-wrap
                  tw-gap-2
                  tw-pl-6
                  tw-pr-14
                  tw-relative
                  tw-overflow-hidden
                  tw-max-h-6
                "
                data-testid="ai-ocr-document-list-all"
              >
                <DocumentPill
                  v-for="item in documentsWithoutLast"
                  :key="item.id"
                  :document="item"
                  :is-disabled="isDisabled"
                  @import-document:delete="handleImportDocumentDelete"
                ></DocumentPill>
                <SfVisibilityChecker
                  v-if="lastDocument"
                  @visibility-change="handleLastDocumentVisibilityChange"
                >
                  <DocumentPill
                    :document="lastDocument"
                    :is-disabled="isDisabled"
                    @import-document:delete="handleImportDocumentDelete"
                  ></DocumentPill>
                </SfVisibilityChecker>
                <div
                  v-show="showMore"
                  class="
                    tw-text-sf-gray-darker tw-absolute tw-right-2 tw-mt-[2px]
                  "
                >
                  {{ I18n.t('ai_ocr_documents.banner.show_more') }}
                </div>
              </div>
            </div>
          </div>
          <div v-tooltip="buttonTooltip" class="tw-flex tw-items-center">
            <component
              :is="buttonComponent"
              :disabled="isDisabled"
              :variant="buttonVariant"
              data-testid="import-documents-button"
              @click="handleImportClicked"
            >
              <span class="icon" :class="buttonIconStyling"></span>
              {{ buttonText }}
            </component>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="expanded"
      class="tw-flex tw-flex-wrap tw-gap-4 tw-p-4 tw-bg-white tw-rounded-b-lg"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { StoredDocument, UIState } from './../types'

import { ref, computed, watch } from 'vue'

import DocumentPill from './document-pill.vue'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'
import SfVisibilityChecker from '@app/shared/vue/components/sf-visibility-checker.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

interface Props {
  uiState?: UIState
  title: string
  subtitle?: string
  documents: StoredDocument[]
  isExpanded?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  uiState: UIState.Initial,
  subtitle: '',
  isExpanded: true,
  documents: () => [],
})

const I18n = useI18n()
const showMore = ref(false)
const expanded = props.isExpanded ? ref(true) : ref(false)

const buttonComponent = computed(() => {
  return hasError.value ? SfButtonInverted : SfButton
})

const buttonVariant = computed(() => {
  return hasError.value ? 'danger' : 'primary'
})

const borderRound = computed(() => {
  return expanded.value ? 'tw-rounded-t-lg' : 'tw-rounded-lg'
})

const documentsWithoutLast = computed(() => {
  return props.documents.length > 1 ? props.documents.slice(0, -1) : []
})

const lastDocument = computed(() => {
  return props.documents.length > 1
    ? props.documents[props.documents.length - 1]
    : props.documents[0]
})

const isDisabled = computed(() => {
  return props.documents.length === 0 || isLoading.value
})

const isLoading = computed(() => {
  return (
    props.uiState === UIState.Loading || props.uiState === UIState.WaitingForMB
  )
})

const hasError = computed(() => {
  return props.uiState === UIState.Error
})

const buttonIconStyling = computed(() => {
  return {
    'sf-spinner sf-spinner-m sf-spinner-primary !tw-h-2 !tw-w-2':
      isLoading.value,
    'icon-exclamation-circle': hasError.value,
    'icon-magic': !hasError.value && !isLoading.value,
  }
})

const chevronStyling = computed<string>(() => {
  return expanded.value ? 'tw-rotate-90' : ''
})

const buttonTooltip = computed<string>(() => {
  if (hasError.value) {
    return I18n.t('ai_ocr_documents.banner.button_tooltip.failed')
  }
  if (isDisabled.value) {
    return I18n.t('ai_ocr_documents.banner.button_tooltip.disabled')
  }

  return ''
})

const buttonText = computed<string>(() => {
  if (isLoading.value) {
    return I18n.t('ai_ocr_documents.banner.loading')
  }

  if (hasError.value) {
    return I18n.t('ai_ocr_documents.banner.retry')
  }

  return I18n.t('ai_ocr_documents.banner.auto_import')
})

const testId = computed<string>(() => {
  return `import-banner-${props.uiState}`
})

const noDocuments = computed(() => {
  return props.documents.length === 0
})

watch(noDocuments, (newValue) => {
  if (newValue) {
    expanded.value = true
  }
})

const emit =
  defineEmits<{
    'import-banner:clicked': [value: boolean]
    'import-document:delete': [value: number | string]
  }>()

function handleImportClicked() {
  emit('import-banner:clicked', true)
}

function handleImportDocumentDelete(id: number | string) {
  emit('import-document:delete', id)
}

function handleLastDocumentVisibilityChange(isVisible: boolean) {
  showMore.value = !isVisible
}

function toggleExpand() {
  expanded.value = !expanded.value
}
</script>
